import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    DateSelector,
    ToggleGroup,
    SelectBox,
    Separator,
    Avatar,
    Button,
    Card,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    Clock,
    Video,
    Info
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const ClockIcon = createKitIcon(Clock);

const VideoIcon = createKitIcon(Video);

const InfoIcon = createKitIcon(Info);

const SLOTS = [
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30"
];

const BUSY_SLOTS = [
    "09:30",
    "10:00",
    "12:30",
    "13:00",
    "14:30"
];

const ZONES = [
    "istanbul",
    "london",
    "berlin",
    "newYork"
];

const FIRST_DAY = new Date(2026, 10, 2);

const LAST_DAY = new Date(2026, 11, 31);

const VIEW_DATE = new Date(2026, 10, 1);

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100%",
        flexGrow: 1
    },
    links: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    owner: {
        alignItems: "flex-start"
    },
    confirmRow: {
        flexDirection: "row"
    },
    card: {
        width: "100%"
    },
    layout: {
        width: "100%"
    },
    times: {
        minHeight: 0,
        flexGrow: 0
    },
    section: {
        minWidth: 0
    },
    slot: {
        flex: 1
    }
});

const handlePress = () => undefined;

const getOptionTitle = (option: {
    name: string;
}) => option.name;

const getOptionKey = (option: {
    id: string;
}) => option.id;

const Booking01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        date,
        setDate
    ] = useState<Date | undefined>(new Date(2026, 10, 18));

    const [
        slot,
        setSlot
    ] = useState<string | undefined>("11:30");

    const [
        hourFormat,
        setHourFormat
    ] = useState("24");

    const [
        isConfirmed,
        setIsConfirmed
    ] = useState(false);

    const isStacked = width < shapeConfigs.wideBreakpoint;

    const dayFormat = new Intl.DateTimeFormat(activeLocale, {
        weekday: "long",
        day: "numeric",
        month: "long"
    });

    const timeFormat = new Intl.DateTimeFormat(activeLocale, {
        hour12: hourFormat === "12",
        minute: "2-digit",
        hour: "2-digit"
    });

    const zones = ZONES.map((key) => ({
        name: t(`blocks.booking01.zone.${key}`),
        id: key
    }));

    const formatSlot = (value: string) => {
        const [
            hours = 0,
            minutes = 0
        ] = value.split(":").map(Number);

        return timeFormat.format(new Date(2026, 10, 18, hours, minutes));
    };

    const handleDateSelect = (value?: Date) => {
        setDate(value);

        setSlot(undefined);

        setIsConfirmed(false);
    };

    const handleHourFormatChange = (value: string | Array<string>) => setHourFormat(typeof value === "string" && value !== "" ? value : hourFormat);

    const handleSlotSelect = (value: string) => () => {
        setSlot(value);

        setIsConfirmed(false);
    };

    const handleConfirm = () => setIsConfirmed(true);

    const renderSlot = (value: string) => {
        if(value === slot) {
            return <View
                style={[
                    stylesheet.confirmRow,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
                key={value}
            >
                <Button
                    onPress={handleSlotSelect(value)}
                    spreadBehaviour="stretch"
                    title={formatSlot(value)}
                    style={stylesheet.slot}
                    accessibilityState={{
                        selected: true
                    }}
                    variant="tonal"
                    type="neutral"
                />
                <Button
                    title={t(isConfirmed ? "blocks.booking01.confirmed" : "blocks.booking01.confirm")}
                    spreadBehaviour="stretch"
                    isDisabled={isConfirmed}
                    onPress={handleConfirm}
                    style={stylesheet.slot}
                    variant="filled"
                    type="primary"
                />
            </View>;
        }

        return <Button
            isDisabled={BUSY_SLOTS.includes(value) || date === undefined}
            onPress={handleSlotSelect(value)}
            title={formatSlot(value)}
            spreadBehaviour="stretch"
            variant="outline"
            type="neutral"
            key={value}
        />;
    };

    return <ScrollView
        contentContainerStyle={[
            stylesheet.page,
            {
                backgroundColor: colors.content.container.mid,
                padding: spaces.spacingLg,
                gap: spaces.spacingSm
            }
        ]}
    >
        <Card
            style={[
                stylesheet.card,
                {
                    maxWidth: shapeConfigs.wideBreakpoint
                }
            ]}
            variant="elevated"
        >
            <View
                style={[
                    stylesheet.layout,
                    {
                        flexDirection: isStacked ? "column" : "row"
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.section,
                        {
                            width: isStacked ? "100%" : shapeConfigs.sideListWidth,
                            padding: spaces.spacingLg,
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <BrandMark/>
                    <View
                        style={[
                            stylesheet.owner,
                            {
                                paddingTop: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Avatar
                            title={t("blocks.booking01.owner")}
                            size="large"
                        />
                        <View
                            style={{
                                gap: spaces.spacingXs
                            }}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.booking01.owner")}
                            </Text>
                            <Text
                                variant="headlineSmallSize"
                                accessibilityRole="header"
                                color="high"
                            >
                                {t("blocks.booking01.meeting")}
                            </Text>
                        </View>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.booking01.about")}
                        </Text>
                    </View>
                    <View>
                        <Item
                            size="xSmall"
                        >
                            <Item.Media
                                icon={ClockIcon}
                                variant="plain"
                            />
                            <Item.Content>
                                <Item.Title>
                                    {t("blocks.booking01.duration")}
                                </Item.Title>
                            </Item.Content>
                        </Item>
                        <Item
                            size="xSmall"
                        >
                            <Item.Media
                                icon={VideoIcon}
                                variant="plain"
                            />
                            <Item.Content>
                                <Item.Title>
                                    {t("blocks.booking01.platform")}
                                </Item.Title>
                            </Item.Content>
                        </Item>
                        <Item
                            size="xSmall"
                        >
                            <Item.Media
                                icon={InfoIcon}
                                variant="plain"
                            />
                            <Item.Content>
                                <Item.Description>
                                    {t("blocks.booking01.form")}
                                </Item.Description>
                            </Item.Content>
                        </Item>
                    </View>
                </View>
                <Separator
                    orientation={isStacked ? "horizontal" : "vertical"}
                    length="100%"
                />
                <View
                    style={[
                        stylesheet.section,
                        {
                            width: isStacked ? "100%" : shapeConfigs.cardWidth + spaces.spacingXxl,
                            padding: spaces.spacingLg,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Text
                        accessibilityRole="header"
                        variant="titleMediumSize"
                        color="high"
                    >
                        {t("blocks.booking01.pickDate")}
                    </Text>
                    <DateSelector
                        selectMultipleObject={handlePress}
                        selectObject={handleDateSelect}
                        isShowTodayIndicator={true}
                        viewDate={VIEW_DATE}
                        minDate={FIRST_DAY}
                        maxDate={LAST_DAY}
                        isShowCard={false}
                        variant="single"
                        date={date}
                    />
                </View>
                <Separator
                    orientation={isStacked ? "horizontal" : "vertical"}
                    length="100%"
                />
                <View
                    style={[
                        stylesheet.section,
                        {
                            flex: isStacked ? undefined : 1,
                            padding: spaces.spacingLg,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.header,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            accessibilityRole="header"
                            variant="titleMediumSize"
                            color="high"
                        >
                            {date ? dayFormat.format(date) : t("blocks.booking01.noDate")}
                        </Text>
                        <ToggleGroup
                            title={t("blocks.booking01.hourFormat")}
                            onValueChange={handleHourFormatChange}
                            attachBehaviour="attached"
                            isClearEnabled={false}
                            value={hourFormat}
                            variant="outline"
                            size="small"
                        >
                            <ToggleGroup.Item
                                title={t("blocks.booking01.hours12")}
                                value="12"
                            />
                            <ToggleGroup.Item
                                title={t("blocks.booking01.hours24")}
                                value="24"
                            />
                        </ToggleGroup>
                    </View>
                    <SelectBox
                        title={t("blocks.booking01.timeZone")}
                        titleExtractor={getOptionTitle}
                        keyExtractor={getOptionKey}
                        spreadBehaviour="stretch"
                        defaultValue="istanbul"
                        data={zones}
                    />
                    <ScrollView
                        style={[
                            stylesheet.times,
                            {
                                maxHeight: controlHeights.large * SLOTS.length / 2
                            }
                        ]}
                        nestedScrollEnabled={true}
                        contentContainerStyle={{
                            gap: spaces.spacingSm
                        }}
                    >
                        {SLOTS.map(renderSlot)}
                    </ScrollView>
                </View>
            </View>
        </Card>
        <View
            style={[
                stylesheet.links,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Button
                title={t("blocks.booking01.privacy")}
                onPress={handlePress}
                variant="link"
                type="neutral"
                size="small"
            />
            <Button
                title={t("blocks.booking01.terms")}
                onPress={handlePress}
                variant="link"
                type="neutral"
                size="small"
            />
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.booking01.poweredBy")}
            </Text>
        </View>
    </ScrollView>;
};
export default Booking01;
